/**
 * Real API Key Client using tRPC
 *
 * This client connects to the backend API via tRPC to manage API keys.
 * It implements the ApiKeyApiClient interface expected by frontend-plugin-api-key.
 */

import type {
  ApiKeyApiClient,
  ApiKeyCreateResponse,
  ApiKeyPublic,
  ApiKeyUsageStats,
  CreateApiKeyInput,
} from '@xhubio-saas/frontend-plugin-api-key'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'

/**
 * Creates an API Key client that uses tRPC to communicate with the backend.
 *
 * This function should be called within a React component that has access
 * to the tRPC context (inside TRPCProvider).
 *
 * @returns ApiKeyApiClient implementation using tRPC
 */
export function useApiKeyClient(): ApiKeyApiClient {
  const utils = trpc.useUtils()

  // Get mutation functions
  const createMutation = trpc.apiKey.create.useMutation()
  const revokeMutation = trpc.apiKey.revoke.useMutation()
  const rotateMutation = trpc.apiKey.rotate.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({ createMutation, revokeMutation, rotateMutation })
  mutations.current = { createMutation, revokeMutation, rotateMutation }

  return useMemo(
    () => ({
      async list(options?: { includeSessionKeys?: boolean }): Promise<ApiKeyPublic[]> {
        // Draht ohne superjson: Datumsfelder kommen als ISO-String, der Pakettyp
        // sagt `Date`. Der Cast überbrückt nur diese Abweichung.
        return (await utils.apiKey.list.fetch(options)) as unknown as ApiKeyPublic[]
      },

      async get(id: string): Promise<ApiKeyPublic> {
        // Datumsfelder als ISO-String (siehe list).
        return (await utils.apiKey.get.fetch({ id })) as unknown as ApiKeyPublic
      },

      async create(input: CreateApiKeyInput): Promise<ApiKeyCreateResponse> {
        const result = await mutations.current.createMutation.mutateAsync({
          name: input.name,
          selectedEntitlements: input.selectedEntitlements,
          expiresAt: input.expiresAt,
          rateLimit: input.rateLimit,
          metadata: input.description ? { description: input.description } : undefined,
        })
        // Invalidate cache so list will refetch
        await utils.apiKey.list.invalidate()
        // Datumsfelder als ISO-String (siehe list).
        return result as unknown as ApiKeyCreateResponse
      },

      async revoke(id: string, reason?: string): Promise<{ success: boolean }> {
        const result = await mutations.current.revokeMutation.mutateAsync({ id, reason })
        // Invalidate cache so list will refetch
        await utils.apiKey.list.invalidate()
        return result
      },

      async rotate(id: string): Promise<ApiKeyCreateResponse> {
        const result = await mutations.current.rotateMutation.mutateAsync({ id })
        // Invalidate cache so list will refetch
        await utils.apiKey.list.invalidate()
        // Datumsfelder als ISO-String (siehe list).
        return result as unknown as ApiKeyCreateResponse
      },

      async getAvailableEntitlements(): Promise<string[]> {
        return utils.apiKey.getAvailableEntitlements.fetch()
      },

      async getUsageStats(id: string, period: 'day' | 'week' | 'month'): Promise<ApiKeyUsageStats> {
        // Datumsfelder (`periodStart`, `periodEnd`) als ISO-String (siehe list).
        return (await utils.apiKey.getUsageStats.fetch({
          id,
          period,
        })) as unknown as ApiKeyUsageStats
      },
    }),
    // NUR `utils`. Keine Mutationen — auch nicht als `.mutateAsync`: dass die
    // heute stabil ist, ist ein Implementierungsdetail von TanStack Query, keine
    // zugesagte API. Sonst bekaeme der Memo pro Render einen neuen Client, und
    // jeder Consumer, der einen Effekt auf die Client-Identitaet schluesselt
    // (useApiKeyNames im UsageDashboardWidget), liefe endlos.
    [utils]
  )
}
